import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/home";

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef<number | null>(null);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(
      () => setCurrent((prev) => (prev + 1) % heroSlides.length),
      6000,
    );
    return () => window.clearInterval(timer);
  }, [paused]);

  const goTo = (index: number) => {
    setCurrent((index + heroSlides.length) % heroSlides.length);
  };

  return (
    <section
      className="relative h-[420px] w-full overflow-hidden rounded-lg bg-foreground-950 md:h-[500px]"
      aria-label="焦点内容轮播"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(event) => {
        touchStart.current = event.touches[0].clientX;
      }}
      onTouchEnd={(event) => {
        if (touchStart.current === null) return;
        const delta = event.changedTouches[0].clientX - touchStart.current;
        if (Math.abs(delta) > 45) {
          goTo(current + (delta < 0 ? 1 : -1));
        }
        touchStart.current = null;
      }}
    >
      {heroSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
          aria-hidden={index !== current}
        >
          <img
            src={slide.image}
            alt={`${slide.title} 焦点内容配图`}
            title={`${slide.title} 日本女优影视作品专题`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/15"></div>
          <div className="absolute inset-x-0 bottom-0 p-6 pb-20 md:p-10 md:pb-24">
            <div key={`${slide.id}-${current}`} className="max-w-2xl animate-fade-up">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 font-label text-[11px] text-foreground-950">
                <i className="ri-bookmark-3-line" aria-hidden="true"></i>
                {slide.eyebrow}
              </span>
              <Link to={slide.link} className="group mt-3 block">
                <h2 className="font-heading text-3xl font-black leading-tight text-background-50 md:text-[44px]">
                  {slide.title}
                </h2>
              </Link>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-background-100/85 md:text-base">
                {slide.description}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-4">
                <Link
                  to={slide.link}
                  className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-4 py-2 font-label text-sm font-medium text-foreground-950 transition-colors hover:bg-accent-400"
                >
                  查看详情
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
                <span className="font-label text-xs text-background-100/70">{slide.meta}</span>
              </div>
            </div>
          </div>
        </div>
      ))}

      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-4 p-5 md:p-6">
        <div className="flex items-center gap-2">
          {heroSlides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => goTo(index)}
              aria-label={`切换到第 ${index + 1} 张焦点内容`}
              aria-current={index === current}
              className={`h-1.5 cursor-pointer rounded-full transition-all duration-300 ${
                index === current ? "w-8 bg-accent-500" : "w-4 bg-background-50/40 hover:bg-background-50/70"
              }`}
            ></button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="mr-1 font-label text-[11px] tracking-wider text-background-100/70">
            {String(current + 1).padStart(2, "0")} / {String(heroSlides.length).padStart(2, "0")}
          </span>
          <button
            type="button"
            onClick={() => goTo(current - 1)}
            aria-label="上一张焦点内容"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:border-accent-400 hover:text-accent-300"
          >
            <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            onClick={() => goTo(current + 1)}
            aria-label="下一张焦点内容"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:border-accent-400 hover:text-accent-300"
          >
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </section>
  );
}